import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin, Clock, BadgeCheck, Flame } from "lucide-react";
import { Seo } from "../components/Seo";
import { MaskedLines, Reveal, FadeIn } from "../components/motion";
import Marquee from "../components/Marquee";
import { BUSINESS, SIGNATURES, restaurantLd, websiteLd } from "../data/business";

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section ref={ref} className="relative grain overflow-hidden pt-40 pb-16 sm:pt-44 lg:pt-48 lg:pb-24" data-testid="hero-section">
      <div className="absolute -top-24 -right-24 w-[28rem] h-[28rem] rounded-full bg-amberbrand/15 blur-3xl" aria-hidden="true" />
      <div className="max-w-6xl mx-auto px-4 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <FadeIn>
            <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm font-bold uppercase tracking-[0.22em] text-ink-soft">
              <span className="flex items-center gap-1.5 text-terracotta"><MapPin size={14} /> {BUSINESS.address.street}, Bogor</span>
              <span className="flex items-center gap-1.5"><Clock size={14} /> 09.00–21.00 WIB</span>
            </p>
          </FadeIn>
          <MaskedLines
            lines={[
              <>Bakso Tujuh Pemuda,</>,
              <><span className="italic font-semibold text-terracotta">hangat andalan</span></>,
              <>keluarga Bogor.</>,
            ]}
            className="mt-6 font-display font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl text-ink"
          />
          <Reveal delay={0.55}>
            <p className="mt-6 text-base sm:text-lg text-ink-soft leading-relaxed max-w-xl">
              Bakso urat jumbo dengan topping <strong className="text-ink">tetelan goreng</strong> yang renyah di luar dan gurih lumer di dalam, mie yamin ala Bandung, dan kuah kaldu yang selalu disajikan hangat — diracik fresh di dapur terbuka kami sejak {BUSINESS.established}.
            </p>
          </Reveal>
          <Reveal delay={0.7}>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/menu/"
                data-testid="hero-cta-lihat-menu-button"
                className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-terracotta text-cream font-bold hover:bg-terracotta-dark transition-colors duration-300"
              >
                Lihat Menu
                <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <a
                href={BUSINESS.links.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-whatsapp-button"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border-2 border-ink font-bold text-ink hover:bg-ink hover:text-cream transition-colors duration-300"
              >
                Reservasi WhatsApp
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.85}>
            <div className="mt-8 flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-leaf text-cream text-xs font-bold" data-testid="hero-halal-badge">
                <BadgeCheck size={14} /> Bersertifikat Halal
              </span>
              <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-tint border border-line text-ink text-xs font-bold" data-testid="hero-open-badge">
                <Flame size={14} className="text-terracotta" /> Buka setiap hari
              </span>
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-5 relative">
          <FadeIn delay={0.35}>
            <div className="relative rounded-[2.5rem] rounded-tr-[7rem] overflow-hidden border-4 border-ink shadow-[12px_12px_0_0_#C83B2B]">
              <motion.img
                src={BUSINESS.images.hero}
                alt="Semangkuk bakso urat jumbo dengan kuah kaldu dan tetelan goreng di Bakso Tujuh Pemuda"
                width="1400"
                height="933"
                fetchPriority="high"
                data-testid="hero-image"
                className="w-full aspect-[4/5] object-cover scale-110"
                style={{ y: imgY }}
              />
            </div>
            <div className="absolute -bottom-8 -left-8 w-28 h-28 sm:w-32 sm:h-32" aria-hidden="true">
              <svg viewBox="0 0 100 100" className="w-full h-full animate-spin-slow">
                <defs>
                  <path id="circlePath" d="M 50,50 m -38,0 a 38,38 0 1,1 76,0 a 38,38 0 1,1 -76,0" />
                </defs>
                <circle cx="50" cy="50" r="50" fill="#2E5A44" />
                <text fill="#FDF8F2" fontSize="10.5" fontWeight="700" letterSpacing="2.5">
                  <textPath href="#circlePath">BAKSO TUJUH PEMUDA • BOGOR •</textPath>
                </text>
                <text x="50" y="56" textAnchor="middle" fill="#E68A2E" fontSize="20" fontWeight="900" fontFamily="Fraunces, serif">7</text>
              </svg>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>
  );
}

function Signatures() {
  return (
    <section className="py-20 sm:py-28" data-testid="signature-section">
      <div className="max-w-6xl mx-auto px-4">
        <Reveal>
          <p className="text-sm font-bold uppercase tracking-[0.25em] text-amberbrand">Menu Andalan</p>
          <h2 className="mt-3 font-display font-black tracking-tight text-2xl sm:text-3xl lg:text-4xl max-w-2xl">
            Yang membuat orang kembali ke Jalan Pengadilan.
          </h2>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {SIGNATURES.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.1} className={i % 2 === 1 ? "lg:mt-10" : ""}>
              <Link
                to="/menu/"
                data-testid={`signature-card-${s.id}`}
                className="group block bg-white rounded-3xl border border-line overflow-hidden hover:shadow-[0_20px_50px_-20px_rgba(200,59,43,0.4)] transition-shadow duration-500"
              >
                <div className="overflow-hidden">
                  <img
                    src={s.image}
                    alt={s.alt}
                    width={s.w}
                    height={s.h}
                    loading="lazy"
                    className="w-full aspect-[4/3] object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] font-bold uppercase tracking-widest text-amberbrand">{s.badge}</span>
                    <span className="font-display font-black text-terracotta">{s.price}</span>
                  </div>
                  <h3 className="mt-2 font-display font-bold text-xl">{s.name}</h3>
                  <p className="mt-1.5 text-sm text-ink-soft leading-relaxed">{s.desc}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <Link to="/menu/" data-testid="signature-menu-link" className="group mt-10 inline-flex items-center gap-2 font-bold text-terracotta">
            Lihat menu lengkap
            <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function AboutPreview() {
  return (
    <section className="py-20 sm:py-28 bg-leaf-deep text-cream grain relative overflow-hidden" data-testid="about-preview-section">
      <div className="max-w-6xl mx-auto px-4 grid lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <p className="text-sm font-bold uppercase tracking-[0.25em] text-amberbrand">Dapur Terbuka</p>
          <h2 className="mt-3 font-display font-black tracking-tight text-2xl sm:text-3xl lg:text-4xl">
            Setiap mangkuk diracik di depan mata Anda.
          </h2>
          <p className="mt-5 text-cream/75 leading-relaxed">
            Di balik dinding kaca dapur kami, mie diaduk, kuah dituang, dan adonan bakso digoreng tepat setelah Anda memesan. Kuah kaldu selalu dijaga tetap hangat — karena semangkuk bakso terbaik adalah yang diseruput selagi mengepul.
          </p>
          <Link to="/tentang/" data-testid="about-preview-link" className="group mt-7 inline-flex items-center gap-2 font-bold text-amberbrand">
            Pelajari tentang Bakso Tujuh Pemuda
            <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="rounded-[2.5rem] rounded-bl-[6rem] overflow-hidden border-4 border-cream/20">
            <img
              src={BUSINESS.images.openKitchen}
              alt="Suasana dapur terbuka tempat setiap pesanan diracik"
              width="1200"
              height="800"
              loading="lazy"
              className="w-full aspect-[4/3] object-cover"
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function LocationPreview() {
  return (
    <section className="py-20 sm:py-28" data-testid="location-preview-section">
      <div className="max-w-6xl mx-auto px-4 grid lg:grid-cols-2 gap-12 items-center">
        <Reveal className="order-2 lg:order-1">
          <div className="rounded-[2.5rem] overflow-hidden border border-line shadow-xl">
            <iframe
              src={BUSINESS.links.mapsEmbed}
              title="Peta lokasi Bakso Tujuh Pemuda di Jl. Pengadilan No. 51, Bogor"
              width="600"
              height="420"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="w-full"
              data-testid="location-map-embed"
            />
          </div>
        </Reveal>
        <Reveal delay={0.1} className="order-1 lg:order-2">
          <p className="text-sm font-bold uppercase tracking-[0.25em] text-amberbrand">Lokasi</p>
          <h2 className="mt-3 font-display font-black tracking-tight text-2xl sm:text-3xl lg:text-4xl">
            Seberang Regina Pacis, lima langkah dari hangatnya kuah.
          </h2>
          <address className="mt-5 not-italic text-ink-soft leading-relaxed" data-testid="location-preview-address">
            {BUSINESS.address.full}
            <br />
            <span className="text-sm">{BUSINESS.address.landmark} · {BUSINESS.hoursLabel}</span>
          </address>
          <div className="mt-7 flex flex-wrap gap-3">
            <a
              href={BUSINESS.links.directions}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="location-directions-button"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-terracotta text-cream font-bold hover:bg-terracotta-dark transition-colors duration-300"
            >
              <MapPin size={18} /> Petunjuk Arah
            </a>
            <Link to="/lokasi/" data-testid="location-preview-link" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-ink font-bold hover:bg-ink hover:text-cream transition-colors duration-300">
              Detail Lokasi
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="pb-24 pt-4" data-testid="final-cta-section">
      <div className="max-w-6xl mx-auto px-4">
        <Reveal>
          <div className="relative grain overflow-hidden rounded-[3rem] bg-terracotta text-cream px-6 py-16 sm:p-16 text-center">
            <h2 className="font-display font-black tracking-tight text-3xl sm:text-4xl lg:text-5xl">
              Lapar? Kuahnya sudah <span className="italic font-semibold text-amberbrand">mengepul.</span>
            </h2>
            <p className="mt-4 text-cream/80 max-w-lg mx-auto">
              Datang langsung, reservasi lewat WhatsApp, atau pesan online — tiga jalan menuju semangkuk bakso hangat.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <a href={BUSINESS.links.orderOnline} target="_blank" rel="noopener noreferrer" data-testid="final-cta-order-button" className="px-7 py-3.5 rounded-full bg-cream text-terracotta font-bold hover:bg-tint transition-colors duration-300">
                Pesan Online
              </a>
              <Link to="/menu/" data-testid="final-cta-menu-button" className="px-7 py-3.5 rounded-full border-2 border-cream font-bold hover:bg-cream hover:text-terracotta transition-colors duration-300">
                Lihat Menu
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function HomePage() {
  return (
    <>
      <Seo
        title="Bakso Tujuh Pemuda — Bakso Urat Jumbo & Tetelan Goreng di Bogor"
        description="Bakso Tujuh Pemuda di Jl. Pengadilan No. 51, Bogor Tengah. Bakso urat jumbo dengan tetelan goreng renyah, mie yamin ala Bandung, dan kuah kaldu hangat. Buka setiap hari 09.00–21.00 WIB."
        path="/"
        jsonLd={[restaurantLd, websiteLd]}
      />
      <Hero />
      <Marquee />
      <Signatures />
      <AboutPreview />
      <LocationPreview />
      <FinalCta />
    </>
  );
}
